// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, Injector, ViewChild} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {animate, query, sequence, stagger, style, transition, trigger} from "@angular/animations";
import {NotificationService, SimSystemService, SimUserExtendedService, SimUtilityService} from "@services";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {MediaType, Permissions, SimRole, TagType} from "@models";
import {HttpEventType} from "@angular/common/http";
import {MatCheckboxChange} from "@angular/material/checkbox";

export interface DialogData_UserManagerUploadUsersDialogInput extends MatDialogDataBaseInput {}

export interface DialogData_UserManagerUploadUsersDialogOutput extends MatDialogDataBaseOutput {
	uploadedFiles?: boolean;
}

@Component({
	selector: "sim-user-manager-upload-users-dialog",
	templateUrl: "./user-manager-upload-users-dialog.component.html",
	styleUrls: ["./user-manager-upload-users-dialog.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175, delay: 0}),
		zoomOutOnLeaveAnimation({duration: 175, delay: 0}),
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("200ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
		trigger("anim", [
			transition("* => void", [
				style({
					height: "*",
					opacity: "1",
					transform: "translateX(0)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".1s ease",
						style({
							height: "0",
							opacity: 0,
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
			transition("void => active", [
				style({
					height: "*",
					opacity: "0",
					transform: "translateX(30px)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".1s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: 1,
							transform: "translateX(0)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
		]),
	],
	standalone: false,
})
export class UserManagerUploadUsersDialogComponent
	extends BasicMaterialDialog<
		UserManagerUploadUsersDialogComponent,
		DialogData_UserManagerUploadUsersDialogInput,
		DialogData_UserManagerUploadUsersDialogOutput
	>
	implements AfterViewInit
{
	MediaType = MediaType;
	SimUtilityService = SimUtilityService;
	afterViewInit: boolean;
	@ViewChild("uploadInput", {static: false}) uploadInput: ElementRef;
	@ViewChild("folderUploadInput", {static: false})
	folderUploadInput: ElementRef;
	currentSelectedFiles: (File & {
		mediaUrl?: string;
		newName?: string;
		progress?: number;
		isPlaying?: boolean;
		progressUploading?: number;
		isUploading?: boolean;
	})[];
	acceptedFileTypes: string = "*";
	acceptedFileTypesText: string = "*";
	@ViewChild("audioPlayer") audioPlayerRef: ElementRef<HTMLAudioElement>;
	public uploadingMediaFiles: boolean;
	public uploadedFiles: boolean = false;
	public type: MediaType;
	public replaceAllFlag: boolean = false;
	public replaceMode: boolean = false;

	public simRole: SimRole[] = [];
	public checkedRoles: string[] = [];
	public currentUser = this._simUserService.user;
	public permissionToChangeAllPassword: boolean = false;
	public updateUserPermission: boolean = false;
	public importErrors = undefined;
	tagIds: string[] = [];

	constructor(
		private _simNotificationService: NotificationService,
		private _simUserService: SimUserExtendedService,
		private _translate: TranslateService,
		private _simSystemService: SimSystemService,
		_injector: Injector,
	) {
		super(_injector);
	}

	ngOnInit(): void {}

	async ngAfterViewInit() {
		await this.getSimRoles();
		this.permissionToChangeAllPassword = this._simUserService.hasPermission(Permissions.UserManagement_PermissionToChangeAllPasswords);

		this.updateUserPermission = this._simUserService.hasPermission(Permissions.UserManagement_EditAll);
		this.setupForMediaType();
	}

	public setupForMediaType() {
		this.afterViewInit = true;
		this.acceptedFileTypes = "xlsx";
		this.acceptedFileTypesText = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
	}

	selectFile() {
		if (!this.currentSelectedFiles) {
			this.uploadInput.nativeElement.click();
		} else {
			this.currentSelectedFiles = undefined;
			this.uploadInput.nativeElement.value = "";
			this.uploadInput.nativeElement.click();
		}
	}

	isFileTypeValid(file: File): boolean {
		const fileType = file.type;
		if (
			this.acceptedFileTypesText.includes(fileType) &&
			["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"].includes(fileType)
		) {
			this.type = MediaType.Excel;
			return true;
		}
		this.type = MediaType.Undefined;
		return false;
	}

	handleFileInput(event: Event) {
		const files = (event.target as HTMLInputElement).files;

		if (files?.length === 0) {
			this._simNotificationService.notifyInfo(this._translate.instant(Translations.UPLOAD_MEDIA_DIALOG_NOTIFYERROR));
			this.currentSelectedFiles = undefined;
			return;
		}

		this.currentSelectedFiles = Array.from(files).filter((file) => {
			return this.isFileTypeValid(file);
		});

		this.currentSelectedFiles.forEach((selectedFile) => {
			selectedFile.mediaUrl = URL.createObjectURL(selectedFile);
		});
	}

	uploadFiles() {
		this.uploadedFiles = true;
		this.uploadingMediaFiles = true;
		this.replaceAllFlag = false;
		this.replaceMode = false;
		this.importErrors = undefined;

		this.currentSelectedFiles.forEach(async (file) => {
			file.isUploading = true;

			this._simUserService
				.importUsersFromExcelAsync({
					uploadedFile: file,
					roles: this.checkedRoles,
					tags: this.tagIds,
				})
				.subscribe({
					next: (response) => {
						if (response.type === HttpEventType.Response) {
							if (response.body.success) {
								this._simNotificationService.notifySuccess();

								if (Object.keys(response.body.importErrors).length > 0) {
									console.log("Errors occurred");
									this.importErrors = response.body.importErrors;
								} else {
									this.close({
										action: "ok",
									});
								}
							} else {
								this._simNotificationService.notifyError(response.body.reason);
							}

							file.isUploading = false;
						}
					},
					error: (response) => {
						console.log(response);
					},
				});
		});
		console.log("FINISHED UPLOADING...");
	}

	protected readonly TagType = TagType;

	private async getSimRoles() {
		this.simRole = (await this._simSystemService.getSimRolesForUpsertUser()).filter((x) => !x.isHidden);
	}

	roleChanged(role: string, $event: MatCheckboxChange) {
		if ($event.checked) {
			this.checkedRoles.push(role);
		} else {
			const index = this.checkedRoles.indexOf(role, 0);
			if (index > -1) {
				this.checkedRoles.splice(index, 1);
			}
		}
	}

	protected readonly Object = Object;
	protected readonly undefined = undefined;

	updateTags($event: string[]) {
		this.tagIds = $event;
	}
}
